import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { settingsAPI, generateAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  MessageSquare,
  Loader2,
  Bot,
  Clock,
  Zap,
  Send,
  Save,
  Sparkles,
} from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function EngagementPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingResponse, setTestingResponse] = useState(false);
  const [testComment, setTestComment] = useState("");
  const [generatedResponse, setGeneratedResponse] = useState("");

  const [settings, setSettings] = useState({
    auto_respond: false,
    response_tone: "friendly",
    response_delay_minutes: 5,
    max_responses_per_hour: 10,
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsAPI.getEngagementSettings();
        setSettings(response.data);
      } catch (error) {
        console.error("Failed to fetch engagement settings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateEngagementSettings(settings);
      toast.success("Engagement settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestResponse = async () => {
    if (!testComment) {
      toast.error("Please enter a test comment");
      return;
    }

    setTestingResponse(true);
    try {
      const response = await generateAPI.generateCommentResponse({
        comment: testComment,
        tone: settings.response_tone,
      });
      setGeneratedResponse(response.data.response);
      toast.success("Response generated!");
    } catch (error) {
      toast.error("Failed to generate response");
    } finally {
      setTestingResponse(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-4xl mx-auto space-y-8"
      data-testid="engagement-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-[#00F0FF]" />
            Engagement Bot
          </h1>
          <p className="text-muted-foreground mt-2">
            Configure automated responses to engage with your audience.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] font-bold rounded-xl gap-2 neon-glow"
          data-testid="save-engagement-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Settings
        </Button>
      </div>

      {/* Auto-Respond Toggle */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center">
                  <Bot className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Auto-Respond to Comments</h3>
                  <p className="text-sm text-muted-foreground">
                    Automatically generate and draft responses to new comments
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.auto_respond}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, auto_respond: checked })
                }
                data-testid="auto-respond-switch"
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Response Settings */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#FF0055]" />
              Response Settings
            </CardTitle>
            <CardDescription>
              Customize how the engagement bot responds to comments.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="text-white">Response Tone</Label>
                <Select
                  value={settings.response_tone}
                  onValueChange={(value) =>
                    setSettings({ ...settings, response_tone: value })
                  }
                >
                  <SelectTrigger className="h-12 bg-secondary/50 border-border/50" data-testid="response-tone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="enthusiastic">Enthusiastic</SelectItem>
                    <SelectItem value="witty">Witty</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Response Delay (minutes)
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={60}
                  value={settings.response_delay_minutes}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      response_delay_minutes: parseInt(e.target.value) || 5,
                    })
                  }
                  className="h-12 bg-secondary/50 border-border/50"
                  data-testid="response-delay-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Max Responses/Hour</Label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={settings.max_responses_per_hour}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      max_responses_per_hour: parseInt(e.target.value) || 10,
                    })
                  }
                  className="h-12 bg-secondary/50 border-border/50"
                  data-testid="max-responses-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Test Response Generator */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#00FF94]" />
              Test Response Generator
            </CardTitle>
            <CardDescription>
              Try out the AI response generator with a sample comment.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Sample Comment</Label>
              <Textarea
                placeholder="Enter a sample comment to test the response generator..."
                value={testComment}
                onChange={(e) => setTestComment(e.target.value)}
                className="min-h-[80px] bg-secondary/50 border-border/50"
                data-testid="test-comment-input"
              />
            </div>

            <Button
              onClick={handleTestResponse}
              disabled={testingResponse || !testComment}
              className="bg-gradient-to-r from-[#00F0FF] to-[#FF0055] hover:opacity-90 text-white font-bold rounded-xl gap-2"
              data-testid="generate-test-response-btn"
            >
              {testingResponse ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Generate Response
            </Button>

            {generatedResponse && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-4 bg-[#00FF94]/10 border border-[#00FF94]/30 rounded-xl"
              >
                <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                  <Bot className="w-4 h-4" />
                  Generated Response:
                </p>
                <p className="text-white">{generatedResponse}</p>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Info Card */}
      <motion.div variants={itemVariants}>
        <Card className="bg-gradient-to-r from-[#00F0FF]/10 to-[#FF0055]/10 border-border/50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#00F0FF]/20 flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h4 className="font-semibold text-white mb-1">How it works</h4>
                <p className="text-sm text-muted-foreground">
                  The engagement bot uses AI to analyze incoming comments and generate contextually 
                  relevant responses. Responses are drafted and held for your review before being posted. 
                  This helps maintain authentic engagement while saving you time.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
